import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMapEvents, useMap } from 'react-leaflet';
import api from '../../services/api';
import { MapPin, Plus, Trash2, Edit2, X, Loader2, Crosshair } from 'lucide-react';
import toast from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png' });

const emptyForm = { name: '', address: '', lat: '', lng: '', radius: 100, isDefault: false };
const OGP_BLUE = '#1B3A8C';
const OGP_GOLD = '#F5A800';

function ClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

// Corrige le rendu de la carte dans la fenetre (tuiles grises) apres l'ouverture
function MapResizer() {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 200); return () => clearTimeout(t); }, [map]);
  return null;
}

// Recentre la carte quand les coordonnees changent (clic ou GPS)
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.setView([parseFloat(lat), parseFloat(lng)], map.getZoom());
  }, [lat, lng, map]);
  return null;
}

export default function AdminLocations() {
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [locating, setLocating] = useState(false);
  const [mapCenter] = useState([9.5370, -13.6773]);

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const { data } = await api.get('/admin/locations');
      setLocations(data);
    } catch(e) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lat || !form.lng) { toast.error('Placez le marqueur sur la carte ou utilisez votre position GPS'); return; }
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/admin/locations/${editing}`, form);
        toast.success('Zone mise à jour');
      } else {
        await api.post('/admin/locations', form);
        toast.success('Zone ajoutée');
      }
      setShowModal(false);
      setEditing(null);
      setForm(emptyForm);
      fetchLocations();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette zone ?')) return;
    await api.delete(`/admin/locations/${id}`);
    toast.success('Zone supprimée');
    fetchLocations();
  };

  const handleMapClick = (latlng) => {
    setForm(prev => ({ ...prev, lat: latlng.lat.toFixed(6), lng: latlng.lng.toFixed(6) }));
  };

  const useMyPosition = () => {
    if (!navigator.geolocation) { toast.error('GPS non disponible sur cet appareil'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(prev => ({ ...prev, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        setLocating(false);
        toast.success('Position GPS récupérée');
      },
      () => {
        setLocating(false);
        toast.error("Impossible d'obtenir la position GPS. Activez la localisation.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const set = f => e => setForm(prev => ({ ...prev, [f]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const hasPoint = form.lat && form.lng;

  return (
    <div style={{padding:24,maxWidth:1200,margin:'0 auto'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,color:OGP_BLUE,margin:0}}>Localisations & Géofencing</h1>
          <p style={{color:'#6b7280',fontSize:14,margin:'4px 0 0'}}>Définissez les zones de pointage autorisées</p>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowModal(true); }}
          style={{display:'flex',alignItems:'center',gap:8,background:OGP_GOLD,color:OGP_BLUE,border:'none',
            padding:'10px 18px',borderRadius:12,fontWeight:700,fontSize:14,cursor:'pointer'}}>
          <Plus style={{width:18,height:18}} /> Ajouter une zone
        </button>
      </div>

      {/* Carte pleine largeur */}
      <div style={{background:'white',borderRadius:16,overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.08)',marginBottom:24,border:'1px solid #e5e7eb'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',gap:8}}>
          <MapPin style={{width:18,height:18,color:OGP_GOLD}} />
          <span style={{fontWeight:600,color:OGP_BLUE,fontSize:14}}>Carte des zones de pointage</span>
        </div>
        <div style={{height:420}}>
          <MapContainer center={mapCenter} zoom={13} style={{height:'100%',width:'100%'}}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {locations.map(loc => (
              <React.Fragment key={loc.id}>
                <Marker position={[parseFloat(loc.lat), parseFloat(loc.lng)]}>
                  <Popup><strong>{loc.name}</strong><br />{loc.address}<br />Rayon: {loc.radius}m</Popup>
                </Marker>
                <Circle center={[parseFloat(loc.lat), parseFloat(loc.lng)]} radius={loc.radius}
                  pathOptions={{ color: OGP_BLUE, fillColor: OGP_GOLD, fillOpacity: 0.15 }} />
              </React.Fragment>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Liste des zones */}
      {locations.length === 0 ? (
        <div style={{background:'white',borderRadius:16,padding:40,textAlign:'center',boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
          <MapPin style={{width:40,height:40,color:'#d1d5db',margin:'0 auto 12px'}} />
          <p style={{color:'#9ca3af',fontSize:14}}>Aucune zone définie. Cliquez sur "Ajouter une zone".</p>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:16}}>
          {locations.map(loc => (
            <div key={loc.id} style={{background:'white',borderRadius:16,padding:16,boxShadow:'0 1px 4px rgba(0,0,0,0.08)',border:'1px solid #e5e7eb'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
                  <div style={{width:40,height:40,background:`${OGP_BLUE}15`,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <MapPin style={{width:20,height:20,color:OGP_BLUE}} />
                  </div>
                  <div>
                    <p style={{fontWeight:600,color:OGP_BLUE,margin:0,fontSize:15}}>{loc.name}</p>
                    <p style={{color:'#6b7280',fontSize:12,margin:'2px 0'}}>{loc.address}</p>
                    <p style={{color:OGP_GOLD,fontSize:12,margin:0,fontWeight:600}}>Rayon: {loc.radius}m</p>
                    {loc.isDefault && (
                      <span style={{display:'inline-block',background:OGP_GOLD,color:OGP_BLUE,fontSize:11,padding:'2px 8px',borderRadius:20,fontWeight:700,marginTop:4}}>
                        Zone par défaut
                      </span>
                    )}
                  </div>
                </div>
                <div style={{display:'flex',gap:4}}>
                  <button onClick={() => { setForm(loc); setEditing(loc.id); setShowModal(true); }}
                    style={{padding:6,background:`${OGP_BLUE}10`,border:'none',borderRadius:8,cursor:'pointer',color:OGP_BLUE}}>
                    <Edit2 style={{width:15,height:15}} />
                  </button>
                  <button onClick={() => handleDelete(loc.id)}
                    style={{padding:6,background:'#fee2e2',border:'none',borderRadius:8,cursor:'pointer',color:'#ef4444'}}>
                    <Trash2 style={{width:15,height:15}} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal formulaire avec carte cliquable integree */}
      {showModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'white',borderRadius:20,width:'100%',maxWidth:520,maxHeight:'92vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            {/* Header modal */}
            <div style={{background:OGP_BLUE,padding:'20px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:2}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <MapPin style={{width:20,height:20,color:OGP_GOLD}} />
                <h2 style={{color:'white',fontWeight:700,fontSize:16,margin:0}}>
                  {editing ? 'Modifier la zone' : 'Nouvelle zone de pointage'}
                </h2>
              </div>
              <button onClick={() => setShowModal(false)}
                style={{background:'rgba(255,255,255,0.15)',border:'none',borderRadius:8,padding:6,cursor:'pointer',color:'white'}}>
                <X style={{width:18,height:18}} />
              </button>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} style={{padding:24}}>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:OGP_BLUE,marginBottom:6}}>Nom de la zone *</label>
                <input value={form.name} onChange={set('name')} required
                  placeholder="Ex: Siège OGP, Bureau Kaloum..."
                  style={{width:'100%',padding:'10px 14px',border:`1.5px solid #e5e7eb`,borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box'}}
                  onFocus={e=>e.target.style.borderColor=OGP_BLUE}
                  onBlur={e=>e.target.style.borderColor='#e5e7eb'} />
              </div>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:OGP_BLUE,marginBottom:6}}>Adresse</label>
                <input value={form.address} onChange={set('address')}
                  placeholder="Adresse complète"
                  style={{width:'100%',padding:'10px 14px',border:'1.5px solid #e5e7eb',borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box'}}
                  onFocus={e=>e.target.style.borderColor=OGP_BLUE}
                  onBlur={e=>e.target.style.borderColor='#e5e7eb'} />
              </div>

              {/* Bouton GPS */}
              <button type="button" onClick={useMyPosition} disabled={locating}
                style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginBottom:12,
                  padding:'11px',border:`1.5px solid ${OGP_BLUE}`,borderRadius:10,background:`${OGP_BLUE}0D`,color:OGP_BLUE,
                  fontSize:14,fontWeight:600,cursor:locating?'default':'pointer',opacity:locating?0.7:1}}>
                {locating ? <Loader2 style={{width:16,height:16,animation:'spin 1s linear infinite'}} /> : <Crosshair style={{width:16,height:16}} />}
                {locating ? 'Localisation…' : 'Utiliser ma position actuelle (GPS)'}
              </button>

              {/* Carte cliquable dans la fenetre */}
              <div style={{height:240,borderRadius:12,overflow:'hidden',border:'1.5px solid #e5e7eb',marginBottom:8}}>
                <MapContainer center={hasPoint ? [parseFloat(form.lat), parseFloat(form.lng)] : mapCenter} zoom={hasPoint ? 16 : 13} style={{height:'100%',width:'100%'}}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapResizer />
                  <Recenter lat={form.lat} lng={form.lng} />
                  <ClickHandler onMapClick={handleMapClick} />
                  {hasPoint && <Marker position={[parseFloat(form.lat), parseFloat(form.lng)]} />}
                  {hasPoint && <Circle center={[parseFloat(form.lat), parseFloat(form.lng)]} radius={Number(form.radius) || 100}
                    pathOptions={{ color: OGP_BLUE, fillColor: OGP_GOLD, fillOpacity: 0.15 }} />}
                </MapContainer>
              </div>
              <p style={{fontSize:12,color:'#6b7280',marginBottom:16,fontWeight:500}}>
                👆 Cliquez sur la carte pour placer le marqueur, ou utilisez le bouton GPS ci-dessus.
              </p>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:OGP_BLUE,marginBottom:6}}>Latitude *</label>
                  <input value={form.lat} onChange={set('lat')} required placeholder="9.537000"
                    style={{width:'100%',padding:'10px 14px',border:'1.5px solid #e5e7eb',borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box'}}
                    onFocus={e=>e.target.style.borderColor=OGP_BLUE}
                    onBlur={e=>e.target.style.borderColor='#e5e7eb'} />
                </div>
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:OGP_BLUE,marginBottom:6}}>Longitude *</label>
                  <input value={form.lng} onChange={set('lng')} required placeholder="-13.677300"
                    style={{width:'100%',padding:'10px 14px',border:'1.5px solid #e5e7eb',borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box'}}
                    onFocus={e=>e.target.style.borderColor=OGP_BLUE}
                    onBlur={e=>e.target.style.borderColor='#e5e7eb'} />
                </div>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:12,fontWeight:600,color:OGP_BLUE,marginBottom:6}}>Rayon de géofencing (mètres)</label>
                <input type="number" value={form.radius} onChange={set('radius')} min={10} max={5000}
                  style={{width:'100%',padding:'10px 14px',border:'1.5px solid #e5e7eb',borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box'}}
                  onFocus={e=>e.target.style.borderColor=OGP_BLUE}
                  onBlur={e=>e.target.style.borderColor='#e5e7eb'} />
              </div>
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',marginBottom:24}}>
                <input type="checkbox" checked={form.isDefault} onChange={set('isDefault')}
                  style={{width:18,height:18,accentColor:OGP_BLUE}} />
                <span style={{fontSize:14,color:'#374151',fontWeight:500}}>Définir comme zone par défaut</span>
              </label>
              <div style={{display:'flex',gap:12}}>
                <button type="button" onClick={() => setShowModal(false)}
                  style={{flex:1,padding:'12px',border:'1.5px solid #e5e7eb',borderRadius:12,fontSize:14,fontWeight:600,cursor:'pointer',background:'white',color:'#374151'}}>
                  Annuler
                </button>
                <button type="submit" disabled={loading}
                  style={{flex:1,padding:'12px',border:'none',borderRadius:12,fontSize:14,fontWeight:700,cursor:'pointer',
                    background:OGP_GOLD,color:OGP_BLUE,display:'flex',alignItems:'center',justifyContent:'center',gap:8,opacity:loading?0.7:1}}>
                  {loading && <Loader2 style={{width:16,height:16,animation:'spin 1s linear infinite'}} />}
                  {editing ? 'Mettre à jour' : 'Ajouter la zone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
